import {
    FETCH_GROUPS_START,
    FETCH_GROUPS_SUCCESS,
    FETCH_GROUPS_FAIL,
    FETCH_ADMINISTERED_GROUPS_START,
    FETCH_ADMINISTERED_GROUPS_SUCCESS,
    FETCH_ADMINISTERED_GROUPS_FAIL,
    FETCH_GROUP_REQUESTS_START,
    FETCH_GROUP_REQUESTS_SUCCESS,
    FETCH_GROUP_REQUESTS_FAIL,
    FETCH_UNHANDLED_GROUP_REQUESTS_SUCCESS,
    FETCH_UNHANDLED_GROUP_REQUESTS_FAIL,
    LEAVE_GROUP_START,
    LEAVE_GROUP_SUCCESS,
    LEAVE_GROUP_FAIL,
    DELETE_GROUP_START,
    DELETE_GROUP_SUCCESS,
    DELETE_GROUP_FAIL,
    RESET_GROUPS,
    SEARCH_GROUP_START,
    SEARCH_GROUP_SUCCESS,
    SEARCH_GROUP_FAIL,
    UPDATE_GROUP_SEARCH_KEYWORD,
    JOIN_GROUP_START,
    JOIN_GROUP_SUCCESS,
    JOIN_GROUP_FAIL,
    CANCEL_REQUEST_JOIN_GROUP_SUCCESS,
    CANCEL_REQUEST_JOIN_GROUP_FAIL,
    RECEIVED_GROUP_MEMBER_DATA,
    SET_GROUP_LIST_PAGE_TAB
} from 'actions/ActionTypes';

import { GROUP_SEARCH_LIMIT, JOIN_GROUP_TYPE } from 'utils/groups/GroupsUtil';

const INITIAL_STATE = {
    groups: [],
    groupsFetching: false,
    groupsFetchSuccess: false,
    groupsFetchFail: false,
    administeredGroups: [],
    administeredGroupsFetching: false,
    administeredGroupsFetchSuccess: false,
    administeredGroupsFetchFail: false,
    groupRequests: [],
    groupRequestsFetching: false,
    groupRequestsFetchSuccess: false,
    groupRequestsFetchFail: false,
    groupUnhandledRequests: [],
    groupUnhandledRequestsFetchSuccess: false,
    groupUnhandledRequestsFetchFail: false,
    groupInvitations: [],
    leaveGroupSuccess: false,
    leaveGroupFail: false,
    leaveGroupDone: false,
    deleteGroupSuccess: false,
    deleteGroupFail: false,
    deleteGroupDone: false,
    searchedGroups: [],
    groupSearchKeyword: '',
    groupSearching: false,
    groupSearchSuccess: false,
    groupSearchFail: false,
    groupJoining: false,
    groupJoinSuccess: false,
    groupJoinFail: false,
    groupJoinDone: false,
    hasMoreToSearch: false,
    groupSearchStart: 1,
    groupMemberData: null,
    tab: null
};

const fetchGroupStart = (state) => ({
    ...state,
    groupsFetching: true,
    groupsFetchSuccess: false,
    groupsFetchFail: false
});
const fetchGroupFinished = (isSuccess) => (state, { data }) => ({
    ...state,
    groups: isSuccess ? data || [] : state.groups,
    groupsFetching: false,
    groupsFetchSuccess: isSuccess,
    groupsFetchFail: !isSuccess
});

const fetchAdministeredGroupStart = (state) => ({
    ...state,
    administeredGroupsFetching: true,
    administeredGroupsFetchSuccess: false,
    administeredGroupsFetchFail: false
});
const fetchAdministeredGroupFinished = (isSuccess) => (state, { data }) => ({
    ...state,
    administeredGroups: isSuccess ? data || [] : state.administeredGroups,
    administeredGroupsFetching: false,
    administeredGroupsFetchSuccess: isSuccess,
    administeredGroupsFetchFail: !isSuccess
});

const fetchGroupRequestsStart = (state) => ({
    ...state,
    groupRequestsFetching: false,
    groupRequestsFetchSuccess: false,
    groupRequestsFetchFail: false
});
const fetchGroupRequestsFinished = (isSuccess) => (state, { data }) => ({
    ...state,
    groupRequests: isSuccess ? data || [] : state.groupRequests,
    groupRequestsFetching: false,
    groupRequestsFetchSuccess: isSuccess,
    groupRequestsFetchFail: !isSuccess
});
const fetchUnhandledGroupRequestsFinished = (isSuccess) => (state, { data }) => ({
    ...state,
    groupUnhandledRequests: isSuccess ? data || [] : state.groupRequests,
    groupUnhandledRequestsFetchSuccess: isSuccess,
    groupUnhandledRequestsFetchFail: !isSuccess,
    groupInvitations: isSuccess ? (data || []).filter((request) => !!request.groupInviteId) : state.groupInvitations
});

const leaveGroupStart = (state) => ({
    ...state,
    leaveGroupSuccess: false,
    leaveGroupFail: false,
    leaveGroupDone: false
});
const leaveGroupFinished = (isSuccess) => (state, { groupId }) => ({
    ...state,
    groups: isSuccess ? state.groups.filter((group) => group.id !== groupId) : state.groups,
    administeredGroups: isSuccess
        ? state.administeredGroups.filter((group) => group.id !== groupId)
        : state.administeredGroups,
    leaveGroupSuccess: isSuccess,
    leaveGroupFail: !isSuccess,
    leaveGroupDone: true
});

const deleteGroupStart = (state) => ({
    ...state,
    deleteGroupSuccess: false,
    deleteGroupFail: false,
    deleteGroupDone: false
});
const deleteGroupFinished = (isSuccess) => (state, { groupId }) => ({
    ...state,
    groups: isSuccess ? state.groups.filter((group) => group.id !== groupId) : state.groups,
    administeredGroups: isSuccess
        ? state.administeredGroups.filter((group) => group.id !== groupId)
        : state.administeredGroups,
    searchedGroups: isSuccess ? state.searchedGroups.filter((group) => group.id !== groupId) : state.searchedGroups,
    deleteGroupSuccess: isSuccess,
    deleteGroupFail: !isSuccess,
    deleteGroupDone: true
});

const resetGroups = () => INITIAL_STATE;

const searchGroupStart = (state, { reset }) => ({
    ...state,
    groupSearching: true,
    groupSearchSuccess: false,
    groupSearchFail: false,
    groupSearchStart: reset ? 1 : state.groupSearchStart + GROUP_SEARCH_LIMIT
});

const searchGroupFinished = (isSuccess) => (state, { data, keyword, reset }) => {
    if (!isSuccess) {
        return {
            ...state,
            groupSearchKeyword: keyword,
            groupSearching: false,
            groupSearchSuccess: false,
            groupSearchFail: true,
            hasMoreToSearch: false
        };
    }

    const { groupDTOs } = data;
    /**
     * we fetch one extra group to check if there are more groups to fetch,
     * and keep at most GROUP_SEARCH_LIMIT groups
     */
    const groupsToKeep = groupDTOs.length === GROUP_SEARCH_LIMIT + 1 ? groupDTOs.slice(0, -1) : groupDTOs;
    return {
        ...state,
        searchedGroups: reset ? groupsToKeep : [...state.searchedGroups, ...groupsToKeep],
        groupSearchKeyword: keyword,
        groupSearching: false,
        groupSearchSuccess: true,
        groupSearchFail: false,
        hasMoreToSearch: groupDTOs.length === GROUP_SEARCH_LIMIT + 1,
        showGroupSearchHint: false
    };
};

const updateGroupSearchKeywordAndReset = (state, { keyword }) => ({
    ...state,
    groupSearchKeyword: keyword,
    searchedGroups: [],
    groupSearching: false,
    groupSearchSuccess: false,
    groupSearchFail: false,
    hasMoreToSearch: false,
    showGroupSearchHint: true
});

const joinGroupStart = (state) => ({
    ...state,
    groupJoining: true,
    groupJoinSuccess: false,
    groupJoinFail: false,
    groupJoinDone: false
});

const joinGroupFinished = (isSuccess) => (state, { groupId, joinType, groupRequest }) => {
    if (!isSuccess) {
        return {
            ...state,
            groupJoining: false,
            groupJoinSuccess: false,
            groupJoinFail: true,
            groupJoinDone: true
        };
    }

    // For public group, add the joined group from searchedGroups into groups, so the group button could update to correct status, i.e., 'Member'
    return {
        ...state,
        groupJoining: false,
        groupJoinSuccess: true,
        groupJoinFail: false,
        groupJoinDone: true,
        groups:
            joinType === JOIN_GROUP_TYPE.requestJoin
                ? state.groups
                : [...state.groups, state.searchedGroups.find((group) => group.id === groupId)],
        groupRequests:
            joinType === JOIN_GROUP_TYPE.requestJoin ? [...state.groupRequests, groupRequest] : state.groupRequests,
        groupInvitations:
            joinType === JOIN_GROUP_TYPE.acceptInvitation
                ? state.groupInvitations.filter((invitation) => invitation.groupId !== groupId)
                : state.groupInvitations
    };
};

const cancelRequestJoinGroupFinished = (isSuccess) => (state, { groupId }) => {
    if (isSuccess) {
        return {
            ...state,
            groupRequests: state.groupRequests.filter((group) => group.userGroupId !== groupId)
        };
    }
};

const receiveGroupMemberData = (state, action) => {
    return {
        ...state,
        groupMemberData: action.data
    };
};

const setGroupListPageTab = (state, { tab }) => ({
    ...state,
    tab
});

const handlers = {
    [FETCH_GROUPS_START]: fetchGroupStart,
    [FETCH_GROUPS_SUCCESS]: fetchGroupFinished(true),
    [FETCH_GROUPS_FAIL]: fetchGroupFinished(false),
    [FETCH_ADMINISTERED_GROUPS_START]: fetchAdministeredGroupStart,
    [FETCH_ADMINISTERED_GROUPS_SUCCESS]: fetchAdministeredGroupFinished(true),
    [FETCH_ADMINISTERED_GROUPS_FAIL]: fetchAdministeredGroupFinished(false),
    [FETCH_GROUP_REQUESTS_START]: fetchGroupRequestsStart,
    [FETCH_GROUP_REQUESTS_SUCCESS]: fetchGroupRequestsFinished(true),
    [FETCH_GROUP_REQUESTS_FAIL]: fetchGroupRequestsFinished(false),
    [FETCH_UNHANDLED_GROUP_REQUESTS_SUCCESS]: fetchUnhandledGroupRequestsFinished(true),
    [FETCH_UNHANDLED_GROUP_REQUESTS_FAIL]: fetchUnhandledGroupRequestsFinished(false),
    [LEAVE_GROUP_START]: leaveGroupStart,
    [LEAVE_GROUP_SUCCESS]: leaveGroupFinished(true),
    [LEAVE_GROUP_FAIL]: leaveGroupFinished(false),
    [DELETE_GROUP_START]: deleteGroupStart,
    [DELETE_GROUP_SUCCESS]: deleteGroupFinished(true),
    [DELETE_GROUP_FAIL]: deleteGroupFinished(false),
    [RESET_GROUPS]: resetGroups,
    [SEARCH_GROUP_START]: searchGroupStart,
    [SEARCH_GROUP_SUCCESS]: searchGroupFinished(true),
    [SEARCH_GROUP_FAIL]: searchGroupFinished(false),
    [UPDATE_GROUP_SEARCH_KEYWORD]: updateGroupSearchKeywordAndReset,
    [JOIN_GROUP_START]: joinGroupStart,
    [JOIN_GROUP_SUCCESS]: joinGroupFinished(true),
    [JOIN_GROUP_FAIL]: joinGroupFinished(false),
    [CANCEL_REQUEST_JOIN_GROUP_SUCCESS]: cancelRequestJoinGroupFinished(true),
    [CANCEL_REQUEST_JOIN_GROUP_FAIL]: cancelRequestJoinGroupFinished(false),
    [JOIN_GROUP_FAIL]: joinGroupFinished(false),
    [RECEIVED_GROUP_MEMBER_DATA]: receiveGroupMemberData,
    [SET_GROUP_LIST_PAGE_TAB]: setGroupListPageTab
};

// getters
export const getGroups = (state) => state.groups.groups;
export const getAdministeredGroups = (state) => state.groups.administeredGroups;

// selectors
export const selectGroupSearchKeyword = (state) => state.groups.groupSearchKeyword;
export const selectGroupSearchStart = (state) => state.groups.groupSearchStart;
export const selectGroupsFetchSuccess = (state) => state.groups.groupsFetchSuccess;
export const selectGroupsLeaveGroupSuccess = (state) => state.groups.leaveGroupSuccess;
export const selectGroupInvitations = (state) => state.groups.groupInvitations;

export default function groups(state = INITIAL_STATE, action = {}) {
    const handler = handlers[action.type];

    if (!handler) return state;
    return handler(state, action);
}

export const getGroupMemberData = (state) => state.groups.groupMemberData;
